import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Button, Input, InputNumber, Card, Space, Tag, Avatar, Modal, Form,
  Drawer, Transfer, message, Popconfirm, Typography,
} from 'antd';
import { UserOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import request from '../utils/request';
import { fmtMoney } from '../utils/format';

const { Text } = Typography;

const fmtTime = (v?: string) => (v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-');

// 分销员管理：顶部分销设置卡片 + 分销员列表 + 分配商品抽屉
const Distributors: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);

  // 分销设置
  const [config, setConfig] = useState<any>(null);
  const [validDays, setValidDays] = useState<number>(30);
  const [savingConfig, setSavingConfig] = useState(false);

  // 添加分销员
  const [addOpen, setAddOpen] = useState(false);
  const [userKeyword, setUserKeyword] = useState('');
  const [userList, setUserList] = useState<any[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userLoading, setUserLoading] = useState(false);
  // 开通中的用户 ID：服务端 /user 不支持「排除已开通」，只能靠按钮锁住避免重复提交
  const [enableUserId, setEnableUserId] = useState<number | null>(null);
  const searchTimer = useRef<any>(null);
  // 响应序号：防抖搜索下慢请求可能后到，丢弃过期响应避免列表回跳
  const userReqSeq = useRef(0);

  // 分配商品抽屉
  const [productOpen, setProductOpen] = useState(false);
  const [current, setCurrent] = useState<any>(null);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);

  const fetchConfig = async () => {
    const res: any = await request.get('/distribution/admin/config');
    setConfig(res);
    setValidDays(res?.referralValidDays ?? 30);
  };

  const fetchList = async (p = page, kw = keyword) => {
    setLoading(true);
    try {
      const res: any = await request.get('/distribution/admin/distributors', {
        params: { page: p, pageSize: 10, keyword: kw || undefined },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);
  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);
  // 卸载时清掉防抖定时器，避免向已卸载组件 setState
  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  const handleSaveConfig = async () => {
    if (!validDays || validDays < 1 || validDays > 365) {
      message.warning('绑定有效天数须在 1-365 之间');
      return;
    }
    setSavingConfig(true);
    try {
      const res: any = await request.put('/distribution/admin/config', { referralValidDays: validDays });
      setConfig(res);
      message.success('分销设置已保存');
    } finally {
      setSavingConfig(false);
    }
  };

  // 添加分销员：服务端搜索 + 服务端分页（/user 支持 page/pageSize/keyword）
  const searchUsers = async (kw: string, p = 1) => {
    const seq = ++userReqSeq.current;
    setUserLoading(true);
    try {
      const res: any = await request.get('/user', {
        params: { page: p, pageSize: 10, keyword: kw || undefined },
      });
      if (seq !== userReqSeq.current) return;
      setUserList(res.list || []);
      setUserTotal(res.total || 0);
      setUserPage(p);
    } finally {
      if (seq === userReqSeq.current) setUserLoading(false);
    }
  };

  // 输入防抖：停止输入 400ms 再打服务端，避免每个字符一次请求
  const scheduleSearch = (kw: string) => {
    setUserKeyword(kw);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchUsers(kw, 1), 400);
  };

  const openAdd = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setUserKeyword('');
    setUserList([]);
    setUserTotal(0);
    setAddOpen(true);
    searchUsers('', 1);
  };

  const handleEnable = async (userId: number) => {
    if (enableUserId !== null) return;
    setEnableUserId(userId);
    try {
      await request.put(`/distribution/admin/distributors/${userId}/toggle`, { enabled: true });
      message.success('已开通分销资格');
      setAddOpen(false);
      setPage(1);
      fetchList(1, keyword);
    } finally {
      setEnableUserId(null);
    }
  };

  const handleDisable = async (userId: number) => {
    await request.put(`/distribution/admin/distributors/${userId}/toggle`, { enabled: false });
    message.success('已关闭分销资格');
    fetchList();
  };

  // 分配商品
  // 服务端分页上限 100/页，须逐页拉全量：一次 pageSize=500 会被静默截断到 100，商品多于 100 件时授权列表缺件
  const fetchAllProducts = async (): Promise<any[]> => {
    const all: any[] = [];
    for (let p = 1; p <= 20; p++) {
      const res: any = await request.get('/product', { params: { page: p, pageSize: 100 } });
      all.push(...(res.list || []));
      if (all.length >= (res.total || 0) || (res.list || []).length === 0) break;
    }
    return all;
  };

  const openProducts = async (record: any) => {
    setCurrent(record);
    setProductOpen(true);
    setProductLoading(true);
    try {
      const [products, authRes]: any[] = await Promise.all([
        fetchAllProducts(),
        request.get(`/distribution/admin/distributors/${record.id}/products`),
      ]);
      const keys = (authRes || []).map((a: any) => String(a.productId));
      setAllProducts(products.map((p: any) => {
        const isPoints = p.productKind === 'POINTS';
        const authorized = keys.includes(String(p.id));
        return {
          key: String(p.id),
          title: p.name,
          description: isPoints
            ? '积分商品（不可授权）'
            : p.distributionEnabled ? '已开启分销' : '未开启分销',
          // 未开启分销/积分商品不可新增选择；但已授权的（即便商品后来关闭分销）仍可移除
          disabled: (isPoints || !p.distributionEnabled) && !authorized,
        };
      }));
      setTargetKeys(keys);
    } finally {
      setProductLoading(false);
    }
  };

  const handleSaveProducts = async () => {
    if (!current) return;
    setSavingProducts(true);
    try {
      await request.put(`/distribution/admin/distributors/${current.id}/products`, {
        productIds: targetKeys.map(Number),
      });
      message.success('商品分配已保存');
      setProductOpen(false);
      setCurrent(null);
      fetchList();
    } finally {
      setSavingProducts(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '头像', dataIndex: 'avatar', width: 70,
      render: (v: string) => <Avatar src={v || undefined} icon={<UserOutlined />} />,
    },
    { title: '昵称/姓名', width: 150, render: (_: any, r: any) => r.realName || r.nickname || '-' },
    { title: '手机号', dataIndex: 'phone', width: 130, render: (v: string) => v || '-' },
    {
      title: '授权商品', width: 100,
      render: (_: any, r: any) => <Tag color="blue">{r._count?.distributorProducts ?? 0} 个</Tag>,
    },
    {
      title: '佣金余额', width: 110,
      render: (_: any, r: any) => (
        <Tag color="gold" style={{ fontSize: 13 }}>{fmtMoney(r.commissionAccount?.total)}</Tag>
      ),
    },
    {
      title: '开通时间', dataIndex: 'createdAt', width: 160,
      render: (v: string) => fmtTime(v),
    },
    {
      title: '操作', width: 200, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Space>
          <Button size="small" type="primary" onClick={() => openProducts(r)}>分配商品</Button>
          <Popconfirm title="确认关闭该用户的分销资格？" onConfirm={() => handleDisable(r.id)}>
            <Button size="small" danger>关闭资格</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>分销员管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加分销员</Button>
      </div>

      <Card size="small" title={<Space><SettingOutlined />分销设置</Space>} style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <Space>
            <Text>客户绑定有效期</Text>
            <InputNumber
              min={1} max={365} value={validDays}
              onChange={(v) => setValidDays(v ?? 30)}
              addonAfter="天"
              style={{ width: 140 }}
            />
            <Button type="primary" loading={savingConfig} onClick={handleSaveConfig}>保存</Button>
          </Space>
          {config?.updatedAt && (
            <Text type="secondary">
              最后修改：{fmtTime(config.updatedAt)}
              {config.updatedByName ? `（${config.updatedByName}）` : ''}
            </Text>
          )}
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            说明：用户点击分销员分享链接后，将在该有效期内绑定为分销员的客户；到期后自动解除，可重新绑定。
          </Text>
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="搜索昵称/姓名/手机号"
          style={{ width: 240 }}
          onSearch={(v) => { setKeyword(v); setPage(1); fetchList(1, v); }}
        />
      </div>

      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1000 }}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />

      {/* 添加分销员 */}
      <Modal
        title="添加分销员"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder="搜索昵称/姓名/手机号"
          style={{ marginBottom: 12 }}
          enterButton={false}
          value={userKeyword}
          onChange={(e) => scheduleSearch(e.target.value)}
          onSearch={(v) => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
            searchUsers(v, 1);
          }}
        />
        <Table
          size="small"
          rowKey="id"
          loading={userLoading}
          dataSource={userList}
          pagination={{ current: userPage, total: userTotal, pageSize: 10, onChange: (p) => searchUsers(userKeyword, p) }}
          scroll={{ y: 320 }}
          columns={[
            {
              title: '用户', width: 200,
              render: (_: any, r: any) => (
                <Space>
                  <Avatar size="small" src={r.avatar || undefined} icon={<UserOutlined />} />
                  {r.realName || r.nickname || `用户#${r.id}`}
                </Space>
              ),
            },
            { title: '手机号', dataIndex: 'phone', width: 130, render: (v: string) => v || '-' },
            {
              title: '操作', width: 110,
              render: (_: any, r: any) => (
                r.distributorEnabled
                  ? <Tag color="default">已开通</Tag>
                  : (
                    <Button
                      size="small"
                      type="primary"
                      loading={enableUserId === r.id}
                      disabled={enableUserId !== null && enableUserId !== r.id}
                      onClick={() => handleEnable(r.id)}
                    >
                      开通
                    </Button>
                  )
              ),
            },
          ]}
        />
        <Text type="secondary">
          共 {userTotal} 个用户（当前关键词：{userKeyword || '无'}）；已开通分销资格的用户会标出，不可重复开通。
        </Text>
      </Modal>

      {/* 分配商品 */}
      <Drawer
        title={`分配商品 - ${current?.realName || current?.nickname || `用户#${current?.id}`}`}
        open={productOpen}
        onClose={() => { setProductOpen(false); setCurrent(null); }}
        width={680}
        extra={
          <Space>
            <Button onClick={() => { setProductOpen(false); setCurrent(null); }}>取消</Button>
            <Button type="primary" loading={savingProducts} onClick={handleSaveProducts}>保存</Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            仅「已开启分销」的商品可被选择，积分商品不参与分销。分销员只能分享此处授权的商品，分享其它商品不产生佣金。
          </Text>
        </div>
        <Transfer
          dataSource={allProducts}
          titles={['全部商品', '已授权']}
          targetKeys={targetKeys}
          onChange={(keys) => setTargetKeys(keys as string[])}
          render={(item) => item.title || ''}
          showSearch
          filterOption={(input, item) => (item.title || '').toLowerCase().includes(input.toLowerCase())}
          listStyle={{ width: 290, height: 460 }}
          disabled={productLoading}
        />
      </Drawer>
    </div>
  );
};

export default Distributors;
